import { useRef } from 'react'
import { PreferencesTopNav } from './PreferencesTopNav'
import { PreferencesSideNav } from './PreferencesSideNav'
import { PreferencesPageSlot } from './PreferencesPageSlot'
import { usePreferencesNavigation } from './usePreferencesNavigation'
import { SETTINGS_TREE, slotId, type SlotId, type SubNavId, type TopNavId } from './settings-tree'
import { renderSlot } from './slot-registry'
import './PreferencesShell.scss'

interface PreferencesShellProps {
  /** Host platform — controls whether the Electron-only sections render
   *  (e.g. App-Behavior with Dock/Menubar/Tray) and the surrounding chrome. */
  platform: 'electron' | 'web'
}

/**
 * Top-level Settings shell shared by Electron's standalone Preferences
 * window and the Web app's modal. Renders the TopNav + SideNav, plus all
 * 21 `(TopNav × SubNav)` slots in parallel — only the active slot is
 * visible, the others sit hidden via `display: none`.
 *
 * State persistence mirrors the {@link TabStack} pattern:
 * - **Lazy mount**: a slot is rendered only after the first time it
 *   becomes active. Until then it's an empty container — initial render
 *   cost stays bounded to the default landing SubPage.
 * - **Persistent state**: once activated, the slot's React state, hook
 *   subscriptions, form inputs, and scroll position survive every SubNav
 *   / TopNav switch until the window closes.
 * - **Reset on close**: closing the Preferences window unmounts the
 *   entire shell — reopening starts fresh.
 *
 * Slot-content lookup lives in {@link renderSlot} (see `slot-registry.tsx`).
 */
export function PreferencesShell({ platform }: PreferencesShellProps) {
  const { topNavId, subNavId, setTopNav, setSubNav } = usePreferencesNavigation()
  const activeSlotId = slotId(topNavId, subNavId)

  // Mutated during render (idempotent ref-write — safe, see TabStack for the
  // same pattern). Avoids the one-frame empty-slot flash that a useEffect
  // round-trip would introduce.
  const activatedSlotsRef = useRef<Set<SlotId>>(new Set())
  if (!activatedSlotsRef.current.has(activeSlotId)) {
    activatedSlotsRef.current.add(activeSlotId)
  }

  return (
    <div className="preferences-shell" data-platform={platform}>
      <PreferencesTopNav activeId={topNavId} onSelect={setTopNav} />
      <div className="preferences-shell__body">
        <PreferencesSideNav topNavId={topNavId} activeSubNavId={subNavId} onSelect={setSubNav} />
        <main className="preferences-shell__content">
          {SETTINGS_TREE.flatMap((tab) =>
            tab.subNav.map((sub) => {
              const id = slotId(tab.id as TopNavId, sub.id as SubNavId)
              const isActive = id === activeSlotId
              const wasActivated = activatedSlotsRef.current.has(id)
              return (
                <PreferencesPageSlot
                  key={id}
                  slotId={id}
                  isActive={isActive}
                  wasActivated={wasActivated}
                >
                  {renderSlot({
                    platform,
                    topNavId: tab.id as TopNavId,
                    subNavId: sub.id as SubNavId,
                  })}
                </PreferencesPageSlot>
              )
            }),
          )}
        </main>
      </div>
    </div>
  )
}
